/**
 * M15: Aggregate donation stats per campaign / fundraiser (dashboard + public).
 */

import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import { errorMessage } from "@/lib/errorMessage"

export interface CampaignProgress {
  raised_cents: number
  donor_count: number
  fundraiser_count: number
}

export async function fetchCampaignProgress(campaignId: string): Promise<CampaignProgress> {
  const { data: donationRows, error: dErr } = await supabase
    .from("donations")
    .select("amount, donor_email")
    .eq("campaign_id", campaignId)
    .eq("status", "succeeded")

  if (dErr) throw new Error(dErr.message)

  const rows = donationRows ?? []
  const raised_cents = rows.reduce((s, r) => s + (r.amount ?? 0), 0)
  const donorEmails = new Set(rows.map((r) => (r.donor_email ?? "").toLowerCase()).filter(Boolean))

  const { count, error: cErr } = await supabase
    .from("campaign_fundraisers")
    .select("id", { count: "exact", head: true })
    .eq("campaign_id", campaignId)

  if (cErr) throw new Error(cErr.message)

  return {
    raised_cents,
    donor_count: donorEmails.size,
    fundraiser_count: count ?? 0,
  }
}

export async function fetchFundraiserProgress(fundraiserId: string): Promise<{ raised_cents: number }> {
  const { data, error } = await supabase
    .from("donations")
    .select("amount")
    .eq("fundraiser_id", fundraiserId)
    .eq("status", "succeeded")

  if (error) throw new Error(error.message)
  const raised_cents = (data ?? []).reduce((s, r) => s + (r.amount ?? 0), 0)
  return { raised_cents }
}

export interface LeaderboardEntry {
  fundraiserId: string
  name: string
  personalGoalCents: number | null
  raisedCents: number
}

export async function fetchFundraiserLeaderboard(campaignId: string): Promise<LeaderboardEntry[]> {
  const { data: frs, error: fErr } = await supabase
    .from("campaign_fundraisers")
    .select("id, name, personal_goal_cents")
    .eq("campaign_id", campaignId)

  if (fErr) throw new Error(fErr.message)

  const { data: dRows, error: dErr } = await supabase
    .from("donations")
    .select("fundraiser_id, amount")
    .eq("campaign_id", campaignId)
    .eq("status", "succeeded")
    .not("fundraiser_id", "is", null)

  if (dErr) throw new Error(dErr.message)

  const byFr = new Map<string, number>()
  for (const r of dRows ?? []) {
    const fid = r.fundraiser_id as string
    byFr.set(fid, (byFr.get(fid) ?? 0) + (r.amount ?? 0))
  }

  const entries: LeaderboardEntry[] = (frs ?? []).map((f) => ({
    fundraiserId: f.id,
    name: f.name,
    personalGoalCents: f.personal_goal_cents,
    raisedCents: byFr.get(f.id) ?? 0,
  }))

  entries.sort((a, b) => b.raisedCents - a.raisedCents)
  return entries
}

export function useCampaignProgress(campaignId: string | null) {
  const [progress, setProgress] = useState<CampaignProgress | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!campaignId) {
      setProgress(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      setProgress(await fetchCampaignProgress(campaignId))
    } catch (e) {
      setError(errorMessage(e, "Failed to load progress"))
      setProgress(null)
    } finally {
      setLoading(false)
    }
  }, [campaignId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { progress, loading, error, refresh }
}
